import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Mail, 
  Reply, 
  Trash2, 
  Eye, 
  EyeOff, 
  Send, 
  Clock,
  User,
  Phone,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import AdminLayout from '../../components/admin/AdminLayout'

const MessagesManagement = () => {
  const { messages, addMessageReply, updateMessage, deleteMessage, loadMessages, markNotificationAsRead } = useStore()
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [showReplyModal, setShowReplyModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [replyContent, setReplyContent] = useState('')
  const [filter, setFilter] = useState('all') // 'all', 'unread', 'replied'

  useEffect(() => {
    loadMessages()
  }, [loadMessages])

  const filteredMessages = messages.filter(message => {
    switch (filter) {
      case 'unread':
        return message.status === 'unread'
      case 'replied':
        return message.status === 'replied'
      default:
        return true
    }
  })

  const handleReply = async (messageId) => {
    if (!replyContent.trim()) {
      alert('Veuillez saisir un message de réponse')
      return
    }

    setLoading(true)
    try {
      await addMessageReply(messageId, {
        content: replyContent
      })

      // Mark notification as read if it exists
      const notificationId = `msg-${messageId}`
      markNotificationAsRead(notificationId)

      setReplyContent('')
      setShowReplyModal(false)
      setSelectedMessage(null)
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la réponse:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = (messageId) => {
    updateMessage(messageId, { status: 'read' })
    
    // Mark notification as read if it exists
    const notificationId = `msg-${messageId}`
    markNotificationAsRead(notificationId)
  }

  const handleDeleteMessage = (messageId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      deleteMessage(messageId)
    }
  }

  const openReplyModal = (message) => {
    setSelectedMessage(message)
    setShowReplyModal(true)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'unread':
        return 'bg-red-100 text-red-800'
      case 'read':
        return 'bg-blue-100 text-blue-800'
      case 'replied':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'unread':
        return 'Non lu'
      case 'read':
        return 'Lu'
      case 'replied':
        return 'Répondu'
      default:
        return status
    }
  }

  const ReplyModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <Reply className="w-5 h-5 text-primary-600" />
              </div>
              <h2 className="text-xl font-bold text-black">
                Répondre à {selectedMessage?.name}
              </h2>
            </div>
            <button
              onClick={() => setShowReplyModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {selectedMessage && (
            <div className="space-y-6">
              {/* Message original */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3 mb-3">
                  <User className="w-5 h-5 text-gray-600 mt-1" />
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="font-semibold text-black">{selectedMessage.name}</span>
                      <span className="text-sm text-gray-500">
                        ({selectedMessage.email})
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <MessageSquare className="w-4 h-4" />
                        <span>{selectedMessage.subject}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(selectedMessage.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
              </div>

              {/* Formulaire de réponse */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Votre réponse
                </label>
                <textarea
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Tapez votre réponse ici..."
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors resize-none"
                />
              </div>

              {/* Réponses précédentes */}
              {selectedMessage.replies && selectedMessage.replies.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Réponses précédentes</h4>
                  <div className="space-y-3">
                    {selectedMessage.replies.map((reply) => (
                      <div key={reply.id} className="p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-800">
                            Réponse de l'admin
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatDate(reply.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-blue-700 whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowReplyModal(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => handleReply(selectedMessage.id)}
                  loading={loading}
                  className="flex-1"
                  icon={<Send className="w-4 h-4" />}
                >
                  Envoyer la réponse
                </Button>
              </div>
            </div>
          )}
        </GlassCard>
      </motion.div>
    </motion.div>
  )

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-black">
              Messages des visiteurs
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez les messages envoyés depuis la page contact
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm text-gray-500">
              {filteredMessages.length} message{filteredMessages.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-3">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white/50 text-gray-600 hover:bg-white/70'
            }`}
          >
            Tous ({messages.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'unread' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white/50 text-gray-600 hover:bg-white/70'
            }`}
          >
            Non lus ({messages.filter(m => m.status === 'unread').length})
          </button>
          <button
            onClick={() => setFilter('replied')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === 'replied' 
                ? 'bg-primary-600 text-white' 
                : 'bg-white/50 text-gray-600 hover:bg-white/70'
            }`}
          >
            Répondus ({messages.filter(m => m.status === 'replied').length})
          </button>
        </div>

        {/* Messages List */}
        <div className="space-y-4">
          {filteredMessages.length === 0 ? (
            <GlassCard className="p-12 text-center">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {filter === 'all' ? 'Aucun message reçu' : 
                 filter === 'unread' ? 'Aucun message non lu' : 
                 'Aucune réponse envoyée'}
              </p>
            </GlassCard>
          ) : (
            filteredMessages.map((message, index) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <GlassCard className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-black">{message.name}</h3>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{message.email}</span>
                            {message.phone && (
                              <>
                                <Phone className="w-4 h-4 ml-2" />
                                <span>{message.phone}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                          {getStatusText(message.status)}
                        </span>
                      </div>

                      <div className="mb-3">
                        <h4 className="font-medium text-black mb-2">{message.subject}</h4>
                        <p className="text-gray-600 text-sm whitespace-pre-wrap line-clamp-3">
                          {message.message}
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {formatDate(message.createdAt)}
                        </span>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openReplyModal(message)}
                            icon={<Reply className="w-4 h-4" />}
                          >
                            Répondre
                          </Button>
                          
                          {message.status === 'unread' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleMarkAsRead(message.id)}
                              icon={<Eye className="w-4 h-4" />}
                            >
                              Marquer lu
                            </Button>
                          )}
                          
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteMessage(message.id)}
                            icon={<Trash2 className="w-4 h-4" />}
                          >
                            Supprimer
                          </Button>
                        </div>
                      </div>

                      {/* Show replies if any */}
                      {message.replies && message.replies.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <h5 className="text-sm font-medium text-gray-700 mb-2">
                            Réponses ({message.replies.length})
                          </h5>
                          <div className="space-y-2">
                            {message.replies.slice(0, 2).map((reply) => (
                              <div key={reply.id} className="p-2 bg-blue-50 rounded text-sm">
                                <div className="flex justify-between items-start">
                                  <span className="text-blue-800 font-medium">Admin</span>
                                  <span className="text-xs text-gray-500">
                                    {formatDate(reply.createdAt)}
                                  </span>
                                </div>
                                <p className="text-blue-700 text-xs mt-1 line-clamp-2">
                                  {reply.content}
                                </p>
                              </div>
                            ))}
                            {message.replies.length > 2 && (
                              <p className="text-xs text-gray-500">
                                + {message.replies.length - 2} autres réponses
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))
          )}
        </div>

        {/* Reply Modal */}
        <AnimatePresence>
          {showReplyModal && <ReplyModal />}
        </AnimatePresence>
      </div>
    </AdminLayout>
  )
}

export default MessagesManagement
